import { useState } from 'react';
import { Image } from '../types';
import { imageApi } from '../services/api';
import { useTaskStore } from '../store/taskStore';
import { Trash2, X } from 'lucide-react';

interface ImageGalleryProps {
  taskId: number;
  images: Image[];
}

export default function ImageGallery({ taskId, images }: ImageGalleryProps) {
  const { fetchTasks, addToast } = useTaskStore();
  const [selectedImage, setSelectedImage] = useState<Image | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const handleDelete = async (imageId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Bu görseli silmek istediğinizden emin misiniz?')) return;

    setDeletingId(imageId);
    try {
      await imageApi.deleteImage(imageId);
      await fetchTasks();
      addToast({ type: 'success', message: 'Görsel silindi' });
    } catch (error: any) {
      addToast({ type: 'error', message: error.message });
    } finally {
      setDeletingId(null);
    }
  };

  if (images.length === 0) {
    return (
      <div className="text-center py-8 bg-soft-gray rounded-lg">
        <p className="text-soft-muted">Henüz görsel yok</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((image) => (
          <div
            key={image.id}
            className={`relative group cursor-pointer rounded-lg overflow-hidden shadow-soft hover:shadow-soft-lg transition-shadow ${
              deletingId === image.id ? 'opacity-50' : ''
            }`}
            onClick={() => setSelectedImage(image)}
          >
            <img
              src={image.url}
              alt={image.original_name}
              className="w-full h-48 object-cover"
            />
            <button
              onClick={(e) => handleDelete(image.id, e)}
              className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
              disabled={deletingId === image.id}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50 animate-fade-in"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={selectedImage.url}
            alt={selectedImage.original_name}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
